import { useSyncExternalStore } from 'react';

import {
  applyServiceWorkerUpdate,
  dismissServiceWorkerNotice,
  getServiceWorkerState,
  subscribeServiceWorker,
} from './serviceWorker';

interface ServiceWorkerUpdateState {
  /** A new build is waiting. Show the prompt; never reload on your own. */
  readonly updateAvailable: boolean;
  /** The app shell is cached and the PWA now works offline. */
  readonly offlineReady: boolean;
  /** Activate the waiting worker and reload. */
  readonly applyUpdate: () => void;
  /** Dismiss both notices without updating. */
  readonly dismiss: () => void;
}

/**
 * `registerType: 'prompt'` (D-10 in spirit, and plain safety): the service
 * worker never activates a new build on its own.
 *
 * An auto-update reloads the page whenever a deploy lands. If that happens
 * between a worker tapping "Pointer" and the response coming back, the punch
 * in flight is lost from the UI's point of view — and "a punch is never
 * silently lost" is the one rule this product cannot break. So the shell asks
 * first and the worker decides when.
 *
 * This is now a *reader*. The registration itself belongs to boot
 * (`pwa/serviceWorker.ts`), because a service worker registered by a component
 * only exists on the pages that render it — see that file for the bug that
 * caused.
 */
export function useServiceWorkerUpdate(): ServiceWorkerUpdateState {
  const { updateAvailable, offlineReady } = useSyncExternalStore(
    subscribeServiceWorker,
    getServiceWorkerState,
    getServiceWorkerState,
  );

  return {
    updateAvailable,
    offlineReady,
    applyUpdate: () => {
      void applyServiceWorkerUpdate();
    },
    dismiss: dismissServiceWorkerNotice,
  };
}
